import axios from "axios";
import { getEnvironment } from "../utils/getEnvironment";

const environment = getEnvironment();

const api = axios.create({
    baseURL: environment.api_url,
    timeout: 20_000,
});

export type RGB = { red: number; green: number; blue: number };

export type LogoRequest = {
    brandName: string;
    businessType: string;
    userMessage: string;
    style?: string;
    color: RGB;
    size?: "512x512" | "1024x1024"; // optional, defaults server-side
};

export type LogoResponse = {
    url: string | null;
    meta: {
        brandName: string;
        businessType: string;
        size: "512x512" | "1024x1024";
    };
};

/** Core call: POST /aicompletions/logo */
export async function requestLogoVariant(
    payload: LogoRequest
): Promise<LogoResponse> {
    const { data } = await api.post<LogoResponse>(
        "/aicompletions/logo",
        payload,
        {
            headers: { "Content-Type": "application/json" },
            timeout: 120_000, // <-- image gen + upload can exceed 20s
            maxBodyLength: Infinity,
            maxContentLength: Infinity,
        }
    );
    return data;
}

// ---------- Types ----------
export type TextGenType =
    | "wallpaper_cta"
    | "service_description"
    | "profile_description";

export type AdminTextRequest =
    | {
          type: "wallpaper_cta";
          language?: string;
          userMessage?: string;
      }
    | {
          type: "service_description";
          language?: string;
          userMessage?: string;
          serviceId: string;
      }
    | {
          type: "profile_description";
          language?: string;
          userMessage?: string;
      };

export type AdminTextResponse = {
    text: string;
    meta: { type: TextGenType; language: string };
};

/** Core call: POST /aicompletions/:brand/text */
export async function requestAdminText(
    uuid: string,
    brandKey: string,
    payload: AdminTextRequest
): Promise<AdminTextResponse> {
    const { data } = await api.post<AdminTextResponse>(
        `/aicompletions/${brandKey}/text`,
        payload,
        {
            headers: { "Content-Type": "application/json", "User-Token": uuid },
        }
    );
    return data;
}

export const requestWallpaperCTA = (
    uuid: string,
    brandKey: string,
    language = "English",
    userMessage?: string
) =>
    requestAdminText(uuid, brandKey, {
        type: "wallpaper_cta",
        language,
        userMessage,
    });

export const requestProfileDescription = (
    uuid: string,
    brandKey: string,
    language = "English",
    userMessage?: string
) =>
    requestAdminText(uuid, brandKey, {
        type: "profile_description",
        language,
        userMessage,
    });

export const requestServiceDescription = (
    uuid: string,
    brandKey: string,
    serviceId: string,
    language = "English",
    userMessage?: string
) =>
    requestAdminText(uuid, brandKey, {
        type: "service_description",
        serviceId,
        language,
        userMessage,
    });

const AICompletions = {
    requestLogoVariant,
    requestWallpaperCTA,
    requestServiceDescription,
    requestProfileDescription,
};

export default AICompletions;
